import React, { useState } from 'react'
import { Box, IconButton, Tooltip, Snackbar } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { isolateNumber } from '@/utils/numberDirection'

/** Extract a plain-text string from a cell value. */
export function extractCellText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return ''
  return String(value)
}

type Props = {
  /** The raw cell value (used for copying). */
  value: unknown
  /** Visible content rendered inside the cell. */
  children: React.ReactNode
}

/**
 * Wraps a DataGrid cell and adds a small copy-to-clipboard button on hover.
 * Shows a brief "!הועתק" snackbar on success.
 */
export default function CellWithCopy({ value, children }: Props) {
  const [showCopied, setShowCopied] = useState(false)

  const text = extractCellText(value)

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setShowCopied(true)
    } catch {
      // no-op
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        '&:hover .copy-btn': { opacity: 1 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {isolateNumber(children)}
      </Box>

      {text && (
        <Tooltip title="העתק">
          <IconButton
            className="copy-btn"
            size="small"
            onClick={handleCopy}
            sx={{ opacity: 0, transition: 'opacity 0.15s', ml: 0.5, p: '2px' }}
          >
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}

      <Snackbar
        open={showCopied}
        autoHideDuration={1500}
        onClose={() => setShowCopied(false)}
        message="!הועתק"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}
